/**
 * RoomReactsBridge — the editorial bridge between the personal Memory
 * Wall hero and the rest of the homepage.
 *
 * Carries the second half of the positioning story: not "your memory"
 * but "our night". Three asymmetric vignette tiles — magazine layout,
 * never SaaS-grid:
 *
 *   ┌─────────────────────┐  ┌─────────┐  ┌──────────────┐
 *   │   A memory arrives  │  │ Shared  │  │  Afterwards  │
 *   │      (largest)      │  │ atmos.  │  │  (medium)    │
 *   │                     │  │(smallest│  │              │
 *   │                     │  │ tile)   │  │              │
 *   └─────────────────────┘  └─────────┘  └──────────────┘
 *
 * Each tile is a self-contained SVG/composition that shows ONE piece
 * of the collective story:
 *   1. Left — a polaroid arriving over a dim wall with a halo (the
 *      Hero Moment, captured in still form)
 *   2. Center — a stylised venue projection with silhouettes (shared
 *      atmosphere — every screen in the room sharing the night)
 *   3. Right — a chapter spine + sealed letter resting on a shelf
 *      (the preserved-afterwards loop, capture → room → preservation)
 *
 * Transition from the hero: a soft warm-glow fade-band sits at the
 * top so the bridge reads as physically adjacent to the wall — not
 * a stacked website section.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const SERIF = 'Georgia, "Times New Roman", serif';

export default function RoomReactsBridge() {
  const navigate = useNavigate();

  return (
    <section
      data-testid="bridge-section"
      style={{
        position: 'relative',
        background: '#0E0805',
        color: '#FBEED0',
        padding: '0 0 96px',
        overflow: 'hidden',
      }}
    >
      {/* Continuity band — warm fade from the hero's wall, so the bridge
          reads as physically adjacent rather than a hard section break. */}
      <div aria-hidden style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, height: 120,
        background: 'linear-gradient(180deg, rgba(26,16,8,1) 0%, rgba(14,8,5,0.85) 60%, rgba(14,8,5,0) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Three drifting dust motes continuing from the hero */}
      {[0, 1, 2].map((i) => (
        <div key={`bridge-dust-${i}`} aria-hidden style={{
          position: 'absolute', zIndex: 1, pointerEvents: 'none',
          width: 2 + (i % 2), height: 2 + (i % 2), borderRadius: '50%',
          background: 'rgba(255,230,180,0.30)', filter: 'blur(0.5px)',
          top: `${30 + i * 16}%`, left: '-4%',
          animation: `bridgeDust 26s linear ${i * 7}s infinite`,
        }} />
      ))}

      <div style={{
        position: 'relative', zIndex: 2,
        maxWidth: 1240, margin: '0 auto',
        padding: '120px 28px 0',
      }}>
        {/* Section header — editorial, anchored left (not centered SaaS) */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
          style={{ maxWidth: 720, marginBottom: 64 }}
        >
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: 'rgba(212,170,82,0.78)', marginBottom: 14,
          }}>
            Chapter two ✦
          </div>
          <h2
            data-testid="bridge-headline"
            style={{
              margin: 0,
              fontFamily: SERIF,
              fontSize: 'clamp(32px, 4.8vw, 56px)',
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: '#FBEED0',
              textShadow: '0 0 24px rgba(212,170,82,0.16)',
            }}
          >
            The room reacts together <span style={{ color: '#D4AF5E' }}>✦</span>
          </h2>
          <p style={{
            margin: '20px 0 0',
            maxWidth: 580,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(15px, 1.5vw, 19px)',
            color: 'rgba(255,235,200,0.66)',
            lineHeight: 1.6,
          }}>
            One memory belongs to a guest. The night belongs to everyone in it.
            EventDrop is built for both — the personal keepsake, and the shared atmosphere it lives inside.
          </p>
        </motion.div>

        {/* THREE ASYMMETRIC TILES — magazine layout, never grid */}
        <div className="ed-bridge-grid" style={{
          display: 'grid',
          gridTemplateColumns: '46fr 22fr 32fr',     // largest / smallest / medium
          gridTemplateRows: 'auto',
          gap: 'clamp(20px, 2.6vw, 44px)',
          alignItems: 'end',                          // tiles rest at different heights
        }}>
          {/* ── LEFT — A memory arrives (largest, tallest) */}
          <BridgeTile
            testId="bridge-tile-arrives"
            eyebrow="Live"
            heading="A memory arrives"
            caption="The wall notices every new memory ✦"
            offsetY={0}
            heightPct={1.0}
          >
            <ArrivesScene />
          </BridgeTile>

          {/* ── CENTER — Shared atmosphere (smallest, lifted) */}
          <BridgeTile
            testId="bridge-tile-shared"
            eyebrow="Together"
            heading="Shared atmosphere"
            caption="Every screen in the room becomes part of the night"
            offsetY={42}            // sits higher than its neighbours — magazine breathing
            heightPct={0.78}
          >
            <SharedAtmosphereScene />
          </BridgeTile>

          {/* ── RIGHT — Afterwards (medium, slight lift) */}
          <BridgeTile
            testId="bridge-tile-afterwards"
            eyebrow="Preserved"
            heading="Afterwards"
            caption="Long after the lights go out, the night remains"
            offsetY={16}
            heightPct={0.88}
          >
            <AfterwardsScene />
          </BridgeTile>
        </div>

        {/* Soft footer line — the bridge's thesis, italic */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 1.4 }}
          style={{
            marginTop: 72,
            textAlign: 'center',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(14px, 1.4vw, 17px)',
            color: 'rgba(255,235,200,0.42)',
            letterSpacing: '0.04em',
          }}
        >
          Capture · the room · preservation
        </motion.p>
      </div>

      <style>{`
        @keyframes bridgeDust {
          0%   { transform: translate(0vw, 0) scale(0.8); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate(110vw, -45px) scale(1.1); opacity: 0; }
        }
        @keyframes bridgePolaroidArrive {
          0%   { opacity: 0; transform: translate(-50%, -28px) rotate(-5deg) scale(0.92); }
          50%  { opacity: 1; transform: translate(-50%, 0)    rotate(-3deg) scale(1.0);  }
          85%  { opacity: 1; transform: translate(-50%, 0)    rotate(-3deg) scale(1.0);  }
          100% { opacity: 0; transform: translate(-50%, -10px) rotate(-3deg) scale(0.98); }
        }
        @keyframes bridgeHalo {
          0%, 100% { opacity: 0; }
          30%      { opacity: 0.65; }
          70%      { opacity: 0.65; }
        }
        @keyframes bridgeSlideshow {
          0%, 33%  { opacity: 1; }
          34%, 66% { opacity: 0; }
          67%, 100%{ opacity: 0; }
        }
        @keyframes bridgeSilhouetteSway {
          0%, 100% { transform: translateX(0); }
          50%      { transform: translateX(2px); }
        }
        @keyframes bridgeSpineGlow {
          0%, 100% { filter: brightness(1.0); }
          50%      { filter: brightness(1.10); }
        }
        @media (max-width: 980px) {
          .ed-bridge-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-bridge-scene] *, [data-bridge-scene] {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────
// TILE WRAPPER — editorial vignette frame with italic caption
// ────────────────────────────────────────────────────────────────
function BridgeTile({ testId, eyebrow, heading, caption, children, offsetY = 0, heightPct = 1 }) {
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
      style={{
        position: 'relative',
        transform: `translateY(${-offsetY}px)`,
      }}
    >
      {/* The scene panel — wood-walnut family, hairline gold border, restrained */}
      <div
        data-bridge-scene
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `1 / ${heightPct}`,
          minHeight: 220,
          maxHeight: 460,
          background: `
            radial-gradient(ellipse at 50% 15%, rgba(212,170,82,0.10) 0%, transparent 55%),
            linear-gradient(180deg, #1c1108 0%, #120a06 60%, #0c0604 100%)
          `,
          borderRadius: 12,
          border: '1px solid rgba(212,170,82,0.18)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,235,210,0.04)',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>

      {/* Editorial caption — eyebrow, heading, italic body. Anchored
          beneath each tile with a hairline divider — never inside the
          frame so the scene reads as observed, not labeled. */}
      <div style={{ padding: '20px 4px 0' }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'rgba(212,170,82,0.78)', marginBottom: 6,
        }}>
          {eyebrow}
        </div>
        <div style={{
          fontFamily: SERIF, fontSize: 19, color: '#FBEED0',
          letterSpacing: '-0.005em', marginBottom: 6,
        }}>
          {heading}
        </div>
        <div style={{
          width: 28, height: 1,
          background: 'rgba(212,170,82,0.28)',
          margin: '8px 0 12px',
        }} />
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 14, color: 'rgba(255,235,200,0.66)',
          lineHeight: 1.55, maxWidth: 360,
        }}>
          {caption}
        </div>
      </div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────
// SCENE 1 — A memory arrives. A dim wall with one polaroid haloed.
// ────────────────────────────────────────────────────────────────
function ArrivesScene() {
  // Background polaroids — small, low-contrast, suggesting a wall
  const bg = [
    { x: '10%', y: '52%', rot: -3, src: '/memory-hero/snap-02.jpg' },
    { x: '30%', y: '60%', rot: 1,  src: '/memory-hero/snap-05.jpg' },
    { x: '52%', y: '54%', rot: -2, src: '/memory-hero/snap-08.jpg' },
    { x: '74%', y: '58%', rot: 2,  src: '/memory-hero/snap-11.jpg' },
  ];
  return (
    <>
      {/* Wood plank backdrop on a darker layer so the new polaroid pops */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background: `
          repeating-linear-gradient(
            90deg,
            rgba(40,22,8,0) 0px,
            rgba(40,22,8,0) 72px,
            rgba(40,22,8,0.40) 74px,
            rgba(40,22,8,0.40) 76px
          )
        `,
        opacity: 0.55,
      }} />

      {/* Sparse fairy lights — five bulbs across the top */}
      <svg viewBox="0 0 400 120" preserveAspectRatio="none" style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 80, width: '100%',
      }}>
        <path d="M 10 26 Q 100 60, 200 32 T 390 26"
          fill="none" stroke="rgba(35,22,10,0.85)" strokeWidth="1.3" strokeLinecap="round" />
        {[0, 1, 2, 3, 4].map((i) => {
          const x = 30 + i * 84;
          const y = 32 + Math.sin(i * 1.2) * 10;
          return (
            <g key={i} style={{
              animation: `fairyFlicker ${4 + i * 0.6}s ease-in-out ${i * 0.7}s infinite`,
              opacity: 0.85,
            }}>
              <circle cx={x} cy={y} r="10" fill="rgba(255,210,130,0.30)" filter="blur(3.5px)" />
              <circle cx={x} cy={y} r="3.2" fill="#FFE3A0" />
            </g>
          );
        })}
      </svg>

      {/* Dim background polaroids — visible but quiet */}
      {bg.map((p, i) => (
        <div key={i} aria-hidden style={{
          position: 'absolute', top: p.y, left: p.x,
          width: '17%', aspectRatio: '4 / 5',
          background: `#1a1108 center/cover no-repeat url(${p.src})`,
          padding: '6%',
          transform: `rotate(${p.rot}deg)`,
          boxShadow: '0 6px 12px rgba(0,0,0,0.5)',
          border: '6px solid #F8F1E0',
          borderBottomWidth: 12,
          opacity: 0.55,    // wall is dim while a memory arrives
          transition: 'opacity 400ms ease',
        }} />
      ))}

      {/* The arriving polaroid — center-front, with halo and tape */}
      <div aria-hidden style={{
        position: 'absolute',
        top: '32%', left: '50%',
        width: '22%', aspectRatio: '4 / 5',
        background: `#1a1108 center/cover no-repeat url(/memory-hero/snap-14.jpg)`,
        padding: '6%',
        boxShadow: '0 10px 22px rgba(0,0,0,0.55)',
        border: '7px solid #F8F1E0',
        borderBottomWidth: 18,
        animation: 'bridgePolaroidArrive 6s ease-in-out infinite',
        transformOrigin: 'top center',
      }}>
        {/* Tape */}
        <div aria-hidden style={{
          position: 'absolute',
          top: -8, left: '50%',
          transform: 'translateX(-50%) rotate(-3deg)',
          width: 36, height: 10,
          background: 'rgba(245,230,200,0.82)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          mixBlendMode: 'multiply',
        }} />
      </div>

      {/* Halo glow around the new polaroid */}
      <div aria-hidden style={{
        position: 'absolute',
        top: '32%', left: '50%',
        width: '32%', aspectRatio: '1 / 1',
        transform: 'translate(-50%, -10%)',
        background: 'radial-gradient(circle, rgba(212,170,82,0.35) 0%, transparent 60%)',
        animation: 'bridgeHalo 6s ease-in-out infinite',
        pointerEvents: 'none',
        filter: 'blur(8px)',
      }} />

      {/* Handwritten caption on the arriving polaroid */}
      <div aria-hidden style={{
        position: 'absolute',
        top: '64%', left: '50%',
        transform: 'translateX(-50%) rotate(-3deg)',
        fontFamily: 'Caveat, cursive', fontSize: 'clamp(11px, 1vw, 14px)',
        color: '#A85F60', textShadow: '0 1px 0 rgba(255,255,255,0.5)',
        whiteSpace: 'nowrap',
      }}>
        Speech of the year ♡
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// SCENE 2 — Shared atmosphere. Stylised venue with projected wall.
// ────────────────────────────────────────────────────────────────
function SharedAtmosphereScene() {
  const slides = [
    '/memory-hero/snap-01.jpg',
    '/memory-hero/snap-06.jpg',
    '/memory-hero/snap-12.jpg',
  ];
  return (
    <>
      {/* Venue back wall — projected slideshow */}
      <div aria-hidden style={{
        position: 'absolute',
        top: '10%', left: '10%', right: '10%',
        aspectRatio: '16 / 10',
        background: '#040201',
        border: '1px solid rgba(212,170,82,0.22)',
        boxShadow: '0 0 40px rgba(255,210,140,0.18), inset 0 0 30px rgba(0,0,0,0.6)',
        overflow: 'hidden',
      }}>
        {slides.map((s, i) => (
          <div key={i} aria-hidden style={{
            position: 'absolute', inset: 0,
            background: `#1a1108 center/cover no-repeat url(${s})`,
            opacity: 0,
            animation: `bridgeSlideshow 9s ease-in-out ${i * 3}s infinite`,
          }} />
        ))}
        {/* Subtle "live" indicator dot in the corner */}
        <div aria-hidden style={{
          position: 'absolute', top: 8, right: 10,
          width: 6, height: 6, borderRadius: '50%',
          background: '#5EE17B',
          boxShadow: '0 0 8px rgba(94,225,123,0.8)',
          animation: 'wallPulse 1.6s ease-in-out infinite',
        }} />
      </div>

      {/* Projector beam — soft warm light from above the screen */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: '8%', left: '50%',
        transform: 'translateX(-50%)',
        width: '80%', height: '40%',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(255,210,140,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Silhouettes — three crowd figures, watching the wall */}
      <svg viewBox="0 0 400 80" preserveAspectRatio="none" style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: 70, width: '100%',
        animation: 'bridgeSilhouetteSway 11s ease-in-out infinite',
      }}>
        {/* Floor line glow */}
        <line x1="0" y1="78" x2="400" y2="78"
          stroke="rgba(212,170,82,0.22)" strokeWidth="0.6" />
        {/* Silhouette 1 — couple holding hands */}
        <path d="M 60 80 L 60 50 Q 64 38 70 38 Q 76 38 80 50 L 80 80 Z
                 M 88 80 L 88 50 Q 92 36 100 36 Q 108 36 112 50 L 112 80 Z"
          fill="rgba(0,0,0,0.92)" />
        {/* Silhouette 2 — solo figure mid-frame */}
        <path d="M 180 80 L 180 48 Q 184 32 192 32 Q 200 32 204 48 L 204 80 Z"
          fill="rgba(0,0,0,0.92)" />
        {/* Silhouette 3 — small group */}
        <path d="M 290 80 L 290 50 Q 294 40 300 40 Q 306 40 310 50 L 310 80 Z
                 M 316 80 L 316 52 Q 320 42 326 42 Q 332 42 336 52 L 336 80 Z
                 M 342 80 L 342 54 Q 346 44 352 44 Q 358 44 362 54 L 362 80 Z"
          fill="rgba(0,0,0,0.92)" />
      </svg>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// SCENE 3 — Afterwards. A spine + sealed letter resting on a shelf.
// ────────────────────────────────────────────────────────────────
function AfterwardsScene() {
  return (
    <>
      {/* Shelf surface — bottom of the frame */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0, height: '22%',
        background: `
          linear-gradient(180deg, #3a2615 0%, #1c1108 100%),
          repeating-linear-gradient(
            90deg,
            rgba(40,22,8,0) 0px,
            rgba(40,22,8,0) 36px,
            rgba(40,22,8,0.4) 38px,
            rgba(40,22,8,0.4) 40px
          )
        `,
        borderTop: '1px solid rgba(212,170,82,0.18)',
        boxShadow: 'inset 0 1px 0 rgba(255,235,210,0.05)',
      }} />

      {/* Ambient warm glow over the shelf */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: '18%', left: '50%',
        transform: 'translateX(-50%)',
        width: '75%', height: '55%',
        background: 'radial-gradient(ellipse at 50% 100%, rgba(212,170,82,0.20) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      {/* CHAPTER SPINE (burgundy cloth) */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: '20%', left: '28%',
        width: '7%', height: '54%',
        background: 'linear-gradient(180deg, #4a1b25 0%, #2a0e16 100%)',
        boxShadow: '0 6px 14px rgba(0,0,0,0.55), inset 0 14px 18px rgba(0,0,0,0.3)',
        transform: 'rotate(-1deg)',
        transformOrigin: 'bottom center',
        animation: 'bridgeSpineGlow 7s ease-in-out infinite',
      }}>
        {/* Foil bands */}
        <div aria-hidden style={{
          position: 'absolute', top: '12%', left: 0, right: 0, height: 1.6,
          background: '#E6B855', opacity: 0.7,
        }} />
        <div aria-hidden style={{
          position: 'absolute', bottom: '14%', left: 0, right: 0, height: 1.2,
          background: '#E6B855', opacity: 0.55,
        }} />
        {/* Sideways title */}
        <div style={{
          position: 'absolute', inset: '24% 0 26% 0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          fontFamily: SERIF, fontSize: 9, letterSpacing: '0.18em',
          color: '#E6B855', textTransform: 'uppercase', fontWeight: 500,
        }}>Late Night</div>
      </div>

      {/* SECOND CHAPTER SPINE (midnight cloth, lean opposite) */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: '20%', left: '38%',
        width: '6%', height: '46%',
        background: 'linear-gradient(180deg, #15161B 0%, #0c1126 100%)',
        boxShadow: '0 6px 14px rgba(0,0,0,0.55), inset 0 12px 16px rgba(0,0,0,0.3)',
        transform: 'rotate(1.6deg)',
        transformOrigin: 'bottom center',
        animation: 'bridgeSpineGlow 8s ease-in-out 1.2s infinite',
      }}>
        <div aria-hidden style={{
          position: 'absolute', top: '14%', left: 0, right: 0, height: 1.4,
          background: '#D4AF5E', opacity: 0.6,
        }} />
      </div>

      {/* RECAP LETTER (cream cloth, vertical ribbon + wax seal) */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: '20%', left: '46%',
        width: '4.4%', height: '40%',
        background: 'linear-gradient(180deg, #F4ECD9 0%, #E2D5B6 100%)',
        boxShadow: '0 6px 14px rgba(0,0,0,0.45), 0 0 0 1px rgba(60,40,20,0.20)',
        transform: 'rotate(-0.8deg)',
        transformOrigin: 'bottom center',
        animation: 'bridgeSpineGlow 9s ease-in-out 0.6s infinite',
      }}>
        {/* Top handwritten label band */}
        <div aria-hidden style={{
          position: 'absolute', top: '14%', left: -1, right: -1, height: 12,
          background: 'rgba(255,250,240,0.92)',
          borderTop: '1px solid rgba(80,55,25,0.22)',
          borderBottom: '1px solid rgba(80,55,25,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: SERIF, fontSize: 7, color: '#7A5A2E',
        }}>✦</div>
        {/* Ribbon */}
        <div aria-hidden style={{
          position: 'absolute', top: 0, bottom: 0, left: '50%',
          transform: 'translateX(-50%)', width: 5,
          background: 'linear-gradient(180deg, #B8923F 0%, #B8923Fdd 100%)',
          boxShadow: '0 0 4px rgba(184,146,63,0.45)',
        }} />
        {/* Wax seal */}
        <div aria-hidden style={{
          position: 'absolute', top: '58%', left: '50%',
          width: 10, height: 10,
          transform: 'translate(-50%, -50%) rotate(-12deg)',
          background: 'radial-gradient(circle at 35% 30%, #C2434C 0%, #7A1F26 80%)',
          borderRadius: '50%',
          boxShadow: '0 1px 2px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)',
        }} />
      </div>

      {/* THIRD CHAPTER SPINE (forest cloth) */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: '20%', left: '54%',
        width: '7.5%', height: '52%',
        background: 'linear-gradient(180deg, #1f3a2c 0%, #0e1f17 100%)',
        boxShadow: '0 6px 14px rgba(0,0,0,0.55), inset 0 14px 18px rgba(0,0,0,0.3)',
        transform: 'rotate(-2deg)',
        transformOrigin: 'bottom center',
        animation: 'bridgeSpineGlow 7.5s ease-in-out 2s infinite',
      }}>
        <div aria-hidden style={{
          position: 'absolute', top: '13%', left: 0, right: 0, height: 1.6,
          background: '#C9A86E', opacity: 0.65,
        }} />
        <div aria-hidden style={{
          position: 'absolute', bottom: '15%', left: 0, right: 0, height: 1.2,
          background: '#C9A86E', opacity: 0.5,
        }} />
        <div style={{
          position: 'absolute', inset: '24% 0 26% 0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          fontFamily: SERIF, fontSize: 8.5, letterSpacing: '0.18em',
          color: '#C9A86E', textTransform: 'uppercase', fontWeight: 500,
        }}>The Speeches</div>
      </div>

      {/* Editorial eyebrow inside the scene — soft, never label-y */}
      <div aria-hidden style={{
        position: 'absolute', top: 16, left: 18,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: 'rgba(212,170,82,0.65)',
      }}>
        Tonight's archive ✦
      </div>
    </>
  );
}
